<template>
    <div id="manage-variables" class="p-2">
        <div class="templates-header">
            <h2 class="d-none d-sm-block">
                {{
                    $t("entities.templates.edit-template", {
                        title: selectedTemplate.title,
                    })
                }}
            </h2>
            <p class="d-none d-sm-block">
                {{ $t("entities.templates.message-preview") }}
            </p>

            <perfect-scrollbar class="scroll-area">
                <div class="whatsapp-preview mb-1 overflow-hidden">
                    <div class="text-center">
                        <!-- Image preview -->
                        <img
                            v-if="asset && selectedTemplate.mediaType === 'image'"
                            :alt="asset.originalName"
                            :src="getFullPath(asset)"
                            class="w-100"
                        />
                        <!-- Video preview -->
                        <b-embed
                            v-if="asset && selectedTemplate.mediaType === 'video'"
                            :src="getFullPath(asset)"
                            allowfullscreen
                            controls
                            type="video"
                        />
                    </div>

                    <!-- File preview -->
                    <div v-if="asset && selectedTemplate.mediaType === 'document'" class="mt-50 mx-1">
                        <b-link>
                            <font-awesome-icon :icon="resolveContentTypeIcon(asset.contentType)" size="xl" />
                            {{ asset.originalName }}
                        </b-link>
                    </div>

                    <p class="m-0 p-1">{{ selectedTemplate.content }}</p>
                </div>

                <h3>{{ $t("entities.templates.attachment.title") }}</h3>
                <p>{{ $t("entities.templates.attachment.description") }}</p>

                <b-form-group
                    :label="$t('entities.templates.attachment.label-file')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                >
                    <template v-if="!asset && !selectedTemplate.assetId">
                        <b-button :disabled="isUploadingAsset" variant="primary" @click="openFilePrompt()">
                            <b-spinner v-if="isUploadingAsset" small />
                            <font-awesome-icon
                                v-else
                                :icon="['fas', 'file-plus']"
                                class="cursor-pointer mr-25"
                                size="1x"
                            />
                            Upload attachment
                        </b-button>
                        <b-form-file v-show="false" id="file" :accept="allowedFileTypes" @input="uploadAsset($event)" />
                    </template>

                    <template v-else-if="asset">
                        <b-button
                            v-b-tooltip.hover.top="$t('entities.templates.attachment.hover-remove')"
                            variant="primary"
                            @click="removeAsset()"
                        >
                            <font-awesome-icon :icon="['fas', 'file-xmark']" class="cursor-pointer mr-25" size="1x" />
                            {{ asset.originalName }}
                        </b-button>
                    </template>
                </b-form-group>

                <b-form-group
                    :label="$t('entities.templates.attachment.label-lock')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="asset-is-locked"
                >
                    <b-form-checkbox v-model="assetIsLocked" name="asset-is-locked" switch></b-form-checkbox>
                </b-form-group>

                <div class="templates-footer mt-1 mb-2">
                    <b-row>
                        <b-col cols="6">
                            <b-button type="submit" variant="light" @click="$emit('reset-template')">
                                {{ $t("Back") }}
                            </b-button>
                        </b-col>
                        <b-col cols="6">
                            <b-button class="float-right" type="submit" variant="primary" @click.prevent="submit">
                                {{ $t("Save") }}
                            </b-button>
                        </b-col>
                    </b-row>
                </div>
            </perfect-scrollbar>
        </div>
    </div>
</template>

<script>
import useFileUploads from "@/composables/useFileUploads";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { getFullPath } from "@/utils/assets";
import { resolveContentTypeIcon } from "@/utils/chats";
import { required } from "@validations";
import { computed, onMounted, ref } from "@vue/composition-api";
import {
    BButton,
    BCard,
    BCardBody,
    BCardText,
    BCol,
    BEmbed,
    BForm,
    BFormCheckbox,
    BFormFile,
    BFormGroup,
    BFormInput,
    BLink,
    BRow,
    BSpinner,
    VBTooltip,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        ValidationProvider,
        ValidationObserver,
        required,
        BCardText,
        BFormInput,
        BFormGroup,
        BForm,
        BFormCheckbox,
        BFormFile,
        BRow,
        BCol,
        BButton,
        BCard,
        BCardBody,
        BSpinner,
        BEmbed,
        BLink,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        selectedTemplate: {
            type: Object,
            required: true,
        },
    },
    setup(props, { emit }) {
        const toast = useToast();
        const asset = ref(null);
        const assetIsLocked = ref(Boolean(props.selectedTemplate.assetIsLocked));
        const { getAllowedFileTypes } = useFileUploads();

        onMounted(() => {
            if (props.selectedTemplate.assetId) {
                axios.get(`assets/${props.selectedTemplate.assetId}`).then((response) => (asset.value = response.data));
            }
        });

        const submit = async () => {
            // Save the asset if not already part of the template
            if (!props.selectedTemplate.assetId && asset.value) {
                await axios
                    .post(`assets`, asset.value)
                    .then((response) => (props.selectedTemplate.assetId = response.data.id));
            }

            axios
                .patch(`templates/${props.selectedTemplate.id}`, {
                    assetId: props.selectedTemplate.assetId,
                    assetIsLocked: assetIsLocked.value,
                })
                .then(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.templates.approved-templates"),
                            text: i18n.t("toast.update.success", { entity: i18n.t("entities.templates.template") }),
                            icon: "check",
                            variant: "success",
                        },
                    });

                    emit("reset-template");
                })
                .catch(() =>
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("entities.templates.approved-templates"),
                            text: i18n.t("toast.update.error", { entity: i18n.t("entities.templates.template") }),
                            icon: "times",
                            variant: "danger",
                        },
                    })
                );
        };

        const allowedFileTypes = computed(() => getAllowedFileTypes(props.selectedTemplate.mediaType, "whatsapp"));

        const openFilePrompt = () => {
            document.getElementById("file").click();
        };

        const { uploadFile, clearFiles } = useFileUploads();

        const isUploadingAsset = ref(false);
        const uploadAsset = (event) => {
            isUploadingAsset.value = true;
            clearFiles();

            return uploadFile(event)
                .then((uploadedAsset) => {
                    asset.value = uploadedAsset;
                })
                .catch((error) => {
                    toast({
                        component: toastificationContent,
                        props: {
                            title: i18n.t("upload.failed"),
                            text: i18n.t(error.message),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                })
                .finally(() => {
                    isUploadingAsset.value = false;
                });
        };

        const removeAsset = () => {
            asset.value = null;
            props.selectedTemplate.assetId = null;
        };

        return {
            submit,

            allowedFileTypes,
            openFilePrompt,
            uploadAsset,
            isUploadingAsset,
            asset,
            removeAsset,
            getFullPath,
            resolveContentTypeIcon,
            assetIsLocked,
        };
    },
};
</script>
